import { useId, useState, type FormEvent } from "react";
import { useAccount, useChangePassword } from "../../api/queries";
import { ROLE_LABEL } from "../../auth/roleView";
import { ErrorState, LoadingState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { PageHeader } from "../../components/PageHeader";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";
import "./account.css";

function ProfileCard() {
  const { data, isPending, isError, error, refetch } = useAccount();

  if (isPending) return <LoadingState label="Loading your profile…" />;
  if (isError) {
    return (
      <ErrorState
        description={error instanceof Error ? error.message : "Couldn't load your profile. Check your connection and try again."}
        onRetry={refetch}
      />
    );
  }
  if (!data) return null;

  return (
    <dl className="account-profile">
      <div className="account-profile__row">
        <dt>Username</dt>
        <dd>{data.username}</dd>
      </div>
      <div className="account-profile__row">
        <dt>Display name</dt>
        <dd>{data.displayName}</dd>
      </div>
      <div className="account-profile__row">
        <dt>Role</dt>
        <dd>{ROLE_LABEL[data.role]}</dd>
      </div>
    </dl>
  );
}

function ChangePasswordForm() {
  const currentId = useId();
  const newId = useId();
  const confirmId = useId();
  const { pushToast } = useToast();
  const mutation = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  function reset() {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  }

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit = currentPassword.length > 0 && newPassword.length >= 8 && !mismatch;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;
    mutation.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          pushToast({
            title: "Password changed.",
            description: "Any other active sessions on your account were signed out.",
            tone: "success",
          });
          reset();
          mutation.reset();
        },
        onError: (err) => {
          pushToast({
            title: "Couldn't change your password",
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
    // Handed off to the mutation above; hold none of the three password
    // values in state a moment longer than the request needs.
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  }

  return (
    <form className="account-password-form" onSubmit={handleSubmit} noValidate>
      <label className="form-field" htmlFor={currentId}>
        <span>Current password</span>
        <input
          id={currentId}
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
        />
      </label>

      <label className="form-field" htmlFor={newId}>
        <span>New password</span>
        <input
          id={newId}
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength={8}
        />
        <span className="form-field__hint">At least 8 characters.</span>
      </label>

      <label className="form-field" htmlFor={confirmId}>
        <span>Confirm new password</span>
        <input
          id={confirmId}
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        {mismatch && (
          <span className="form-field__error" role="alert">
            Passwords don't match.
          </span>
        )}
      </label>

      <div className="form-actions">
        <Button type="submit" variant="primary" disabled={mutation.isPending || !canSubmit}>
          {mutation.isPending ? "Changing…" : "Change password"}
        </Button>
      </div>
    </form>
  );
}

export function AccountPage() {
  return (
    <>
      <PageHeader title="Account" description="Your profile and sign-in credential." />

      <section className="account-section" aria-labelledby="account-profile-heading">
        <h2 id="account-profile-heading" className="account-section__title">
          Profile
        </h2>
        <ProfileCard />
      </section>

      <section className="account-section" aria-labelledby="account-password-heading">
        <h2 id="account-password-heading" className="account-section__title">
          Change password
        </h2>
        <p className="account-section__note">
          Changing your password ends any other active sessions on your account.
        </p>
        <ChangePasswordForm />
      </section>
    </>
  );
}
